const TIVIGO_ENDPOINT_URL = 'https://tivigo.store/endpoint.php';
const COUNTRY_LOOKUP_URL = 'https://get.geojs.io/v1/ip/country.json';
let creditRequestSequence = 0;

const buildFreshRequestUrl = (rawUrl) => {
  const url = new URL(rawUrl);
  creditRequestSequence += 1;
  url.searchParams.set('_', `${Date.now()}-${creditRequestSequence}`);
  return url.toString();
};

const fetchFreshCreditData = (fetchImpl, url) => fetchImpl(
  buildFreshRequestUrl(url),
  { cache: 'no-store' },
);

const readStoredJson = (storage, key) => {
  if (!storage) return {};

  try {
    return JSON.parse(storage.getItem(key) || '{}') || {};
  } catch (error) {
    return {};
  }
};

const getBrowserStorage = () => (
  typeof window !== 'undefined' ? window.localStorage : null
);

export const getLegacyCreditIdentity = (userInfo, storage = getBrowserStorage()) => {
  const credentials = readStoredJson(storage, 'xtream_credentials');
  const storedUser = readStoredJson(storage, 'xtream_user');
  const info = userInfo?.user_info || storedUser?.user_info || {};
  const paymentUserId = info.id != null && info.id !== ''
    ? String(info.id)
    : credentials.username || info.username || null;

  return {
    userId: paymentUserId == null ? '' : String(paymentUserId),
    userEmail: info.email || '',
    createdAt: info.created_at || null,
    krediModal: info.kredi_modal || 'otomatik',
  };
};

export const resolveLegacyCreditModalSetting = (setting) => {
  if (setting === 'whatsapp') return { type: 'whatsapp', provider: null };
  if (setting === 'polpay' || setting === 'polypay') {
    return { type: 'polpay', provider: null };
  }
  if (setting === 'lidio') return { type: 'stripe', provider: 'lidio' };
  if (setting === 'tosla') return { type: 'stripe', provider: 'tosla' };
  if (setting === 'eximhost') return { type: 'eximhost', provider: null };
  if (setting === 'kapali') return { type: 'kapali', provider: null };

  // Eski tema tanınmayan veya boş ayarlarda Stripe'a dönüyordu.
  return { type: 'stripe', provider: 'stripe' };
};

export const resolveLegacyCreditSettingByDays = (days, settings = {}) => {
  let key;
  if (days <= 1) key = 'kredi_modal_gun_0_1';
  else if (days <= 3) key = 'kredi_modal_gun_2_3';
  else if (days <= 7) key = 'kredi_modal_gun_4_7';
  else if (days <= 24) key = 'kredi_modal_gun_8_24';
  else if (days <= 60) key = 'kredi_modal_gun_25_60';
  else if (days <= 90) key = 'kredi_modal_gun_61_90';
  else if (days <= 365) key = 'kredi_modal_gun_91_365';
  else key = 'kredi_modal_gun_365_plus';

  return settings[key] || 'stripe';
};

export const resolveLegacyCreditFlowFromData = ({
  userModal,
  createdAt,
  settings,
  isTR = true,
  now = Date.now(),
}) => {
  const selectedUserModal = userModal || 'otomatik';
  if (selectedUserModal !== 'otomatik') {
    return {
      ...resolveLegacyCreditModalSetting(selectedUserModal),
      setting: selectedUserModal,
    };
  }

  if (!settings || !createdAt) {
    return { type: 'stripe', provider: 'stripe', setting: 'stripe' };
  }

  const registrationDate = typeof createdAt === 'number'
    ? new Date(createdAt * 1000)
    : new Date(createdAt);
  const diffDays = Math.floor((now - registrationDate.getTime()) / (1000 * 60 * 60 * 24));
  const ageSetting = resolveLegacyCreditSettingByDays(diffDays, settings);

  if (!isTR && ageSetting !== 'whatsapp' && ageSetting !== 'kapali') {
    const internationalSetting = settings.kredi_modal_yurt_disi || 'normal';
    if (internationalSetting !== 'normal') {
      return {
        ...resolveLegacyCreditModalSetting(internationalSetting),
        setting: internationalSetting,
      };
    }
  }

  return {
    ...resolveLegacyCreditModalSetting(ageSetting),
    setting: ageSetting,
  };
};

export const parseLegacyCreditSettings = (payload) => {
  const rows = payload?.data?.ayarlar;
  if (!payload?.success || !Array.isArray(rows)) return null;

  return rows.reduce((settings, row) => {
    if (row?.ayar_key) settings[row.ayar_key] = row.ayar_value;
    return settings;
  }, {});
};

export const loadLegacyCreditSettings = async (fetchImpl = fetch) => {
  if (typeof fetchImpl !== 'function') throw new Error('Kredi ayarları alınamadı.');

  const response = await fetchFreshCreditData(
    fetchImpl,
    `${TIVIGO_ENDPOINT_URL}?endpoint=get_kredi_modal_ayarlar`,
  );
  const payload = await response.json();
  return parseLegacyCreditSettings(payload);
};

export const resolveLegacyCreditFlow = async ({
  userInfo,
  cachedSettings = null,
  fetchImpl = fetch,
  storage = getBrowserStorage(),
  now = Date.now(),
} = {}) => {
  const identity = getLegacyCreditIdentity(userInfo, storage);
  let freshUser = null;
  let freshUserModal = null;
  let freshSettings = null;
  let isTR = true;
  let countryCode = 'TR';

  try {
    const token = storage?.getItem('xtream_api_token') || '';
    const [userResponse, settingsResponse, countryResponse] = await Promise.all([
      token
        ? fetchFreshCreditData(
          fetchImpl,
          `${TIVIGO_ENDPOINT_URL}?endpoint=user_data&token=${encodeURIComponent(token)}`,
        )
        : Promise.resolve(null),
      fetchFreshCreditData(
        fetchImpl,
        `${TIVIGO_ENDPOINT_URL}?endpoint=get_kredi_modal_ayarlar`,
      ),
      fetchImpl(COUNTRY_LOOKUP_URL).catch(() => null),
    ]);

    if (userResponse) {
      const userPayload = await userResponse.json();
      if (userPayload?.success && userPayload?.data?.user) {
        freshUser = userPayload.data.user;
        freshUserModal = freshUser.kredi_modal || 'otomatik';
      }
    }

    const settingsPayload = await settingsResponse.json();
    freshSettings = parseLegacyCreditSettings(settingsPayload);

    if (countryResponse) {
      const countryPayload = await countryResponse.json();
      countryCode = String(countryPayload?.country || '').toUpperCase();
      isTR = countryCode === 'TR';
    }
  } catch (error) {
    // Eski temada ağ hatası yerel kullanıcı ve önceden alınmış ayarlarla devam ediyordu.
  }

  const settings = freshSettings || cachedSettings;
  const resolvedIdentity = {
    userId: freshUser?.id != null && freshUser.id !== ''
      ? String(freshUser.id)
      : identity.userId,
    userEmail: freshUser?.email || identity.userEmail,
    createdAt: freshUser?.created_at || identity.createdAt,
    krediModal: freshUserModal || identity.krediModal,
  };
  const resolvedFlow = resolveLegacyCreditFlowFromData({
    userModal: resolvedIdentity.krediModal,
    createdAt: resolvedIdentity.createdAt,
    settings,
    isTR,
    now,
  });

  return {
    ...resolvedFlow,
    ...resolvedIdentity,
    userModal: resolvedIdentity.krediModal,
    settings,
    isTR,
    countryCode,
  };
};

export const LEGACY_CREDIT_ENDPOINTS = Object.freeze({
  tivigo: TIVIGO_ENDPOINT_URL,
  country: COUNTRY_LOOKUP_URL,
});
